import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import VisitorNav from "../components/VisitorNav";
import VisitorTopMenuBar from "../components/VisitorTopMenuBar";
import VisitorFooter from "../components/VisitorFooter";
import VisitorMobileBottomNav from "../components/VisitorMobileBottomNav";
import ReaderNav from "../components/ReaderNav";
import ReaderTopMenuBar from "../components/ReaderTopMenuBar";
import ReaderMobileBottomNav from "../components/ReaderMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import RegistrationModal from "../components/RegistrationModal";
import FounderWelcomeModal from "../components/FounderWelcomeModal";
import { BookOpen, Users, Target, Sparkles, Play } from "lucide-react";
import { Seo, PAGE_SEO } from '../components/Seo';

const AboutPage = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showFounderModal, setShowFounderModal] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-50 flex flex-col overflow-x-hidden">
      <Seo {...PAGE_SEO.about} />
      {isAuthenticated ? (
        <>
          <ReaderNav />
          <ReaderTopMenuBar />
        </>
      ) : (
        <>
          <VisitorNav />
          <VisitorTopMenuBar />
        </>
      )}

      <main className="flex-1 pb-20 md:pb-0">
        {/* Hero Section - Two Column Layout */}
        <section className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-500 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4zIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')]"></div>
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-12 sm:py-16 lg:py-20">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              {/* Text Column */}
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-indigo-100/90 font-semibold">
                  About
                </p>
                <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
                  About NewAudia<sup className="text-[0.5em]">TM</sup>
                </h1>
                <p className="mt-4 text-xl sm:text-2xl font-semibold bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 bg-clip-text text-transparent">
                  Read. Listen. Ask.
                </p>
                <p className="mt-6 max-w-xl text-base sm:text-lg lg:text-xl text-indigo-50 leading-relaxed">
                  NewAudia<sup className="text-[0.6em]">TM</sup> is a next-generation listening platform that turns
                  traditional manuscripts into NFD-SmartBook<sup className="text-[0.6em]">TM</sup> experiences—interactive,
                  engagement-aware audio stories designed for how people actually listen
                  today.
                </p>
              </div>

              {/* Image Column */}
              <div className="relative">
                <div className="aspect-[4/3] overflow-hidden rounded-3xl border border-white/20 bg-black/20 shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_17effe54-356c-4367-937e-269458cd2bbe/artifacts/cjssv6xl_Image_6.jpg"
                    alt="Person immersed in listening to an audio story with headphones"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why NewAudia Exists */}
        <section id="why-newaudia" className="mx-auto max-w-7xl px-4 py-12 sm:py-16 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)] lg:items-center">
            {/* Text Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 shadow-lg">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">
                  Why NewAudia<sup className="text-[0.5em]">TM</sup> exists
                </h2>
              </div>

              <div className="space-y-6">
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  Traditional books and audiobooks were never designed for the way we
                  consume content now. They're static, blind to reader behavior, and
                  disconnected from the insights authors and publishers need to grow.
                </p>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  NewAudia<sup className="text-[0.6em]">TM</sup> was created to answer a simple question: what if every book
                  could become a living audio experience—one that adapts to the listener, measures
                  engagement in real time, and gives authors a clear picture of how their
                  work is actually being used?
                </p>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  By transforming manuscripts into NFD-SmartBook<sup className="text-[0.6em]">TM</sup> experiences, NewAudia<sup className="text-[0.6em]">TM</sup>
                  bridges the gap between storytelling, technology, and meaningful
                  analytics—without sacrificing the intimacy of listening.
                </p>
              </div>
            </div>

            {/* Supporting Image */}
            <div className="relative lg:mt-16">
              <div className="aspect-square overflow-hidden rounded-3xl border border-slate-700/60 bg-slate-900/80 shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_17effe54-356c-4367-937e-269458cd2bbe/artifacts/ljt8jot0_Image_4.jpg"
                  alt="Relaxed listener enjoying an immersive story through headphones"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What is NFD-SmartBook */}
        <section className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 border-y border-slate-800">
          <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16 lg:py-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-cyan-600 shadow-lg">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">
                What is an NFD-SmartBook<sup className="text-[0.5em]">TM</sup>?
              </h2>
            </div>

            <div className="space-y-6">
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                At the core of NewAudia<sup className="text-[0.6em]">TM</sup> is our NFD-SmartBook<sup className="text-[0.6em]">TM</sup> format. When an author
                uploads a manuscript, NewAudia<sup className="text-[0.6em]">TM</sup> transforms it into a NewAudia Formatted
                Document (NFD)—a structured, enriched version of the book that understands
                chapters, pacing, and context.
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                From that NFD, we generate an NFD-SmartBook<sup className="text-[0.6em]">TM</sup>: an interactive audio experience
                with intelligent navigation, AI-generated summaries, and engagement-aware 
                playback. And with Audia—our AI reading companion—listeners can ask questions
                about the story, characters, and themes as they listen, turning passive consumption
                into active exploration.
              </p>

              {/* Feature Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6 hover:border-emerald-500/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-2 w-2 rounded-full bg-emerald-400"></div>
                    <h3 className="text-lg font-semibold text-slate-50">Audio-First</h3>
                  </div>
                  <p className="text-sm text-slate-400">
                    Professional AI-generated narration with chapter navigation—no recording studio required
                  </p>
                </div>

                <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6 hover:border-cyan-500/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-2 w-2 rounded-full bg-cyan-400"></div>
                    <h3 className="text-lg font-semibold text-slate-50">Meet Audia</h3>
                  </div>
                  <p className="text-sm text-slate-400">
                    Your AI reading companion who answers questions about the story as you listen
                  </p>
                </div>

                <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6 hover:border-purple-500/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-2 w-2 rounded-full bg-purple-400"></div>
                    <h3 className="text-lg font-semibold text-slate-50">Engagement Analytics</h3>
                  </div>
                  <p className="text-sm text-slate-400">
                    Authors see real-time data on listening patterns, completion rates, and reader engagement
                  </p>
                </div>

                <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6 hover:border-indigo-500/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-2 w-2 rounded-full bg-indigo-400"></div>
                    <h3 className="text-lg font-semibold text-slate-50">Publish in Minutes</h3>
                  </div>
                  <p className="text-sm text-slate-400">
                    Upload your EPUB, customize the outline, and your audiobook is ready—same day
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Are */}
        <section className="mx-auto max-w-4xl px-4 py-12 sm:py-16 lg:py-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 shadow-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">
              Who's behind NewAudia<sup className="text-[0.5em]">TM</sup>
            </h2>
          </div>

          <div className="space-y-6">
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              NewAudia<sup className="text-[0.6em]">TM</sup> was created by people who have lived on both sides of the story:
              as authors trying to reach readers, and as technologists building systems
              that scale. We understand the gaps between writing a book, publishing it,
              and truly knowing whether it connected.
            </p>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Our team blends decades of experience in software engineering, AI,
              publishing, and entrepreneurship. We're not trying to replace authors or
              narrators—we're building the rails that let them thrive in a digital world
              where attention is fragmented and data matters.
            </p>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              NewAudia<sup className="text-[0.6em]">TM</sup> exists because we believe books deserve better tools, and
              authors deserve clear insight into the impact of their work.
            </p>
          </div>

          {/* Values Grid */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-600 mb-4">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-50 mb-2">Author-First</h3>
              <p className="text-sm text-slate-400">
                Every decision we make prioritizes the needs of authors and publishers
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-cyan-600 to-blue-600 mb-4">
                <Target className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-50 mb-2">Data-Driven</h3>
              <p className="text-sm text-slate-400">
                We believe meaningful insights lead to better stories and stronger connections
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-emerald-600 to-green-600 mb-4">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-50 mb-2">Story-Focused</h3>
              <p className="text-sm text-slate-400">
                Technology should enhance storytelling, not replace the human craft
              </p>
            </div>
          </div>
        </section>


        {/* Meet the Founder */}
        <section className="mx-auto max-w-4xl px-4 py-8 sm:py-10">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-50">
            Meet the founder
          </h2>

          <div className="mt-5 grid gap-6 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] sm:items-center">
            {/* Text column */}
            <div>
              <h3 className="text-lg font-semibold text-slate-50">
                R. Dale Brunet
              </h3>
              <p className="mt-2 text-xs uppercase tracking-[0.22em] text-slate-400">
                Founder, NewAudia<sup className="text-[0.5em]">TM</sup>
              </p>
              <p className="mt-4 text-sm sm:text-base text-slate-300">
                R. Dale Brunet is a lifelong technologist, author, and entrepreneur who has spent decades moving between servers, saucepans, and stories. From building PC networks and writing software for companies of all sizes, to running a Cajun restaurant in Colorado and publishing books about AI and human nature, his work has always circled the same idea: tools should serve people, not the other way around. NewAudia<sup className="text-[0.5em]">TM</sup> is his answer to a broken reading ecosystem—a way to turn static manuscripts into living NFD-SmartBook<sup className="text-[0.5em]">TM</sup> experiences that respect readers' time and give authors real insight into how their work lands.
              </p>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setShowFounderModal(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-purple-500/25 transition-all"
                  data-testid="hear-founder-story-btn"
                >
                  <Play className="w-4 h-4" />
                  Hear the Founder's Story
                </button>
              </div>
            </div>

            {/* Founder image */}
            <div className="sm:justify-self-end w-full max-w-xs">
              <div className="aspect-square w-full overflow-hidden rounded-3xl border border-slate-700/70 bg-slate-900/80 shadow-xl shadow-black/50">
                <img
                  src="/r-dale-brunet-headshot.jpg"
                  alt="R. Dale Brunet, Founder of NewAudia"
                  className="h-full w-full object-cover"
                  data-testid="about-founder-headshot"
                />
              </div>
              <a
                href="https://www.RDBRUNET.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 transition-colors"
                data-testid="about-founder-website-link"
              >
                www.RDBRUNET.com
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
            </div>
          </div>
        </section>

        {/* Founder's Other Ventures */}
        <section className="mx-auto max-w-4xl px-4 py-8 sm:py-10">
          <h2 className="text-xl font-semibold tracking-tight text-slate-50 mb-6">
            More from R. Dale Brunet
          </h2>
          <div className="flex flex-wrap gap-3">
            <a 
              href="https://www.UWYGPublishing.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 transition-all"
            >
              Use What You Got Publishing
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
            <a 
              href="https://www.AIMeansBusiness.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 transition-all"
            >
              A.I. Means Business
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
            <a 
              href="https://www.DigitalInkAI.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 transition-all"
            >
              Digital Ink AI
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          </div>
        </section>

        {/* Where We're Going */}
        <section className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border-t border-slate-800">
          <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16 lg:py-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-600 to-orange-600 shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">
                The future of reading with NewAudia<sup className="text-[0.5em]">TM</sup>
              </h2>
            </div>

            <div className="space-y-6">
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                We see a future where books are not locked in static formats but live as
                evolving, interactive experiences. Where readers can move seamlessly
                between text and audio, and authors can see more than just sales numbers.
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                NewAudia<sup className="text-[0.6em]">TM</sup> is the foundation for that future. As we grow, we'll continue to
                expand what NFD-SmartBook<sup className="text-[0.6em]">TM</sup> experiences can do—deeper analytics, richer
                personalization, and new ways for authors to connect with the people who
                care about their work.
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                This is just the beginning. If you care about storytelling, reader
                experience, and what comes after the traditional audiobook, you're in the
                right place.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowRegistrationModal(true)}
                className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                data-testid="about-get-started-btn"
              >
                Get Started Free — 10 Credits Included
              </button>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-slate-200 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 hover:border-slate-600 transition-all"
              >
                Read the FAQ
              </Link>
            </div>

            {/* Contact Prompt */}
            <div className="mt-8 text-center">
              <p className="text-sm text-slate-400">
                Questions? Interested in publishing? Reach out at{' '}
                <a href="mailto:info@newaudia.com" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                  info@newaudia.com
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      {isAuthenticated ? (
        <>
          <UniversalFooter />
          <ReaderMobileBottomNav />
        </>
      ) : (
        <>
          <VisitorFooter />
          <VisitorMobileBottomNav />
        </>
      )}

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
      />

      {/* Founder Welcome Modal */}
      <FounderWelcomeModal
        isOpen={showFounderModal}
        onClose={() => setShowFounderModal(false)}
      />
    </div>
  );
};

export default AboutPage;
